/**
 * The topic catalog (D226, E7): the things a player may ASK ABOUT that are
 * not here — what they know of, from the rooms they have walked.
 *
 * @remarks
 * `ask X about Y` refused any Y that was not in the room: the per-turn
 * catalog (D19b) lists what is referable HERE, and the drum in the clearing
 * was no more a word in the hollow than a thing that never existed — "You
 * see no drum here." Asking about absent things is the point of `ask about`
 * (Calder's play-test, E7); a question is not an act on the thing, and
 * needs no reach to it.
 *
 * What a player may ask about is what they have MET: every thing that would
 * be in the catalog if they stood in a room they have visited, as the world
 * is now — so a thing a room still hides (`roomVisibilityCondition` unmet)
 * is not a topic, a thing they have taken is a topic by being carried, and a
 * thing they set down in another room is a topic where they left it. The
 * visited rooms are the honest line: the parser never leaks a thing the
 * player has not seen through a which-one question, and the same rule holds
 * for topics. Nothing the pack never placed is a topic; nothing in an
 * unvisited room is.
 *
 * Every entry is marked `absent`, so the parser knows where it is and the
 * engine knows what to permit: the topic of an `ask` (its bound `about`
 * phrase) may be absent; nothing else may. D231's topic words ride the same
 * rule, under the same mark. A room the player has visited is
 * not a topic — a room's own `askNarratives` answer where the player stands
 * (D137), and a room elsewhere has no sentence for it.
 *
 * A topic stands BELOW everything present ({@link SALIENCE_TOPIC}): Drumline
 * names Sylvain twice — the man in the hollow and his absence everywhere
 * else — and `ask Sylvain about the drum`, read with the topics in, must
 * pick the man who is here over the word for his absence, without asking.
 * Two absent things of one name still tie, and a tie among absences is no
 * question worth asking: the reading is not adopted and the honest miss
 * stands.
 *
 * Deterministic: visited rooms in visit order, each room's entries in its
 * catalog order, each thing once.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';
import type { EntityRef, ParseCandidateEntity } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { topicWordsAskableHere } from '../conversation/topics.js';
import { deriveEntityCatalog } from './derive-entity-catalog.js';
import { entityRefForTopicWord, isBodyRef, roomIdForEntityRef } from './entity-ref.js';
import { pronounAntecedentsOf, withPartnerPronounReserved } from './partner-pronoun.js';

/** A topic's salience: below every present thing and the room itself, above the hands. */
export const SALIENCE_TOPIC = 0.25;

/**
 * D245 (A7): a topic WORD's salience — above the absent things it shares a
 * word with, by more than the which-one window, so the answer somebody here
 * has outranks the things that are not here. It never meets a present name:
 * a word a present thing answers to is not offered (`topicWordsAskableHere`).
 */
export const SALIENCE_TOPIC_WORD = 0.45;

/**
 * The topics: things known from visited rooms and not in the present catalog
 * — and, since D231, the WORDS the things present here declare topics under
 * (`topicWordsAskableHere`): a subject that is no thing at all, askable
 * because something here has an answer for it.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param presentCatalog - This turn's catalog, already derived (what is here is never also a topic)
 * @returns The absent, known things, each once, marked `absent`; then the topic words
 */
export const deriveTopicCatalog = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	presentCatalog: readonly ParseCandidateEntity[],
): readonly ParseCandidateEntity[] => {
	const knownRefs = new Set<string>(presentCatalog.map((entry) => entry.entityRef));
	const topics: ParseCandidateEntity[] = [];

	for (const visitedRoomId of state.progression.visitedRoomIds) {
		if (visitedRoomId === state.progression.activeRoomId) {
			continue;
		}

		// The catalog as the player would see it standing THERE, now — the one
		// derivation, so visibility, containment and droppage are judged by the
		// same rules the room itself would apply.
		const standingThere: RuntimeState = { ...state, progression: { ...state.progression, activeRoomId: visitedRoomId }};

		for (const entry of deriveEntityCatalog(bundle, standingThere)) {
			if (isThing(entry.entityRef) && !knownRefs.has(entry.entityRef)) {
				knownRefs.add(entry.entityRef);
				topics.push({ ...entry, location: 'absent', salience: SALIENCE_TOPIC });
			}
		}
	}

	// D231: the words present things declare topics under — askable because
	// something here has an answer, absent because they are nowhere.
	const topicWords = topicWordsAskableHere(bundle, presentCatalog).map(
		(word): ParseCandidateEntity => ({
			adjectives: [],
			aliases: [],
			entityRef: entityRefForTopicWord(word),
			location: 'absent',
			nouns: [word],
			salience: SALIENCE_TOPIC_WORD,
		}),
	);

	// D235: a topic WORD that is the conversational partner's pronoun (`her` among the keys about EURYDICE) yields to the partner.
	return withPartnerPronounReserved([...topics, ...topicWords], pronounAntecedentsOf(bundle, state));
};

/**
 * Is this topic-catalog entry an absent thing (D226, E7)?
 *
 * @param topics - The topic catalog
 * @param entityRef - A resolved reference
 * @returns Whether the reference names one of the topics
 */
export const isTopicRef = (topics: readonly ParseCandidateEntity[], entityRef: EntityRef): boolean =>
	topics.some((topic) => topic.entityRef === entityRef);

/** A thing, as opposed to the room (D41) or the hands (D63), neither of which is a topic elsewhere. */
const isThing = (entityRef: EntityRef): boolean => !isBodyRef(entityRef) && roomIdForEntityRef(entityRef) === undefined;
